import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, TrendingUp, Users, DollarSign, Target, CheckCircle, Briefcase, FileText, Handshake, Building2, UserPlus, Shield, RefreshCw, Network, Banknote, Sparkles, BarChart3, Send, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';

// Animated Counter Component
const AnimatedCounter = ({ end, prefix = '', suffix = '', duration = 2000 }) => {
  const [count, setCount] = useState(end);
  const ref = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          let start = 0;
          const increment = end / (duration / 16);
          const timer = setInterval(() => {
            start += increment;
            if (start >= end) {
              setCount(end);
              clearInterval(timer);
            } else {
              setCount(Math.floor(start));
            }
          }, 16);
        }
      },
      { threshold: 0.3 }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration, hasAnimated]);

  return <span ref={ref}>{prefix}{count}{suffix}</span>;
};

const Home = () => {
  const navigate = useNavigate();

  const stats = [
    { label: 'Funds Raised', value: 5, prefix: '$', suffix: 'M+', icon: Banknote, gradient: 'from-emerald-500 via-green-500 to-teal-500', glow: 'shadow-emerald-500/50' },
    { label: 'Startups Served', value: 50, suffix: '+', icon: Building2, gradient: 'from-amber-400 via-orange-500 to-red-500', glow: 'shadow-orange-500/50' },
    { label: 'Active Projects', value: 15, icon: Sparkles, gradient: 'from-blue-500 via-indigo-500 to-purple-500', glow: 'shadow-indigo-500/50' },
    { label: 'Deals This Month', value: 8, icon: Handshake, gradient: 'from-rose-500 via-pink-500 to-fuchsia-500', glow: 'shadow-pink-500/50' },
  ];

  const industries = [
    'Fintech', 'Healthcare', 'EdTech', 'SaaS', 'E-commerce',
    'Blockchain', 'AI/ML', 'CleanTech', 'BioTech', 'Enterprise Software'
  ];

  const expertise = [
    { icon: DollarSign, title: 'Funding for Startups', description: 'End-to-end support for startups raising capital from angel to Series A', gradient: 'from-emerald-400 to-green-600', bgPattern: 'from-emerald-50 to-green-50' },
    { icon: BarChart3, title: 'Funding for Funds', description: 'Help venture funds and investment vehicles raise capital from LPs', gradient: 'from-blue-400 to-indigo-600', bgPattern: 'from-blue-50 to-indigo-50' },
    { icon: Send, title: 'Deal Origination', description: 'Connect investors with high-quality investment opportunities', gradient: 'from-amber-400 to-orange-600', bgPattern: 'from-amber-50 to-orange-50' },
    { icon: Handshake, title: 'Mergers & Acquisitions', description: 'Strategic advisory for M&A transactions and exit opportunities', gradient: 'from-purple-400 to-violet-600', bgPattern: 'from-purple-50 to-violet-50' },
    { icon: FileText, title: 'Documentation', description: 'Professional pitch decks, financial models, and data rooms', gradient: 'from-rose-400 to-pink-600', bgPattern: 'from-rose-50 to-pink-50' }
  ];

  const whyChoose = [
    { icon: Target, title: 'We Understand You First', description: 'Before we take on any engagement, we deeply understand your business, stage, and fundraising needs. No cookie-cutter approaches.' },
    { icon: CheckCircle, title: 'Brutally Honest', description: "We'll tell you upfront if we can help or not. We won't waste your time or money if we're not the right fit for your journey." },
    { icon: Shield, title: 'Private One-to-One Communication', description: 'Unlike marketplaces that publicly display fundraising details, we maintain complete confidentiality with private, direct communication.' },
    { icon: RefreshCw, title: 'Unlimited Iteration Support', description: 'Plans change, projections evolve. We support unlimited document iterations because we understand startup dynamics.' },
    { icon: Network, title: 'Build Your Investor Network', description: 'The connections you make through us become your network for future rounds and long-term relationships.' },
    { icon: UserPlus, title: 'Family Offices & Angel Access', description: 'Direct connections to hard-to-reach family offices and angel investors who are actively seeking opportunities.' },
    { icon: Users, title: 'Round-the-Clock Support', description: 'We know founders work at all hours. Our team is available 24/7 to support you when you need us most.' },
    { icon: Briefcase, title: 'End-to-End Partnership', description: 'From initial preparation to closing the deal, we provide comprehensive support tailored to your specific needs and stage.' }
  ];

  const process = [
    { step: '01', title: 'Discovery Call', description: 'We learn about your business, traction, and fundraising goals' },
    { step: '02', title: 'Honest Assessment', description: 'We evaluate if we can genuinely help and provide transparent feedback' },
    { step: '03', title: 'Strategy Design', description: 'We craft a customized fundraising strategy and investor targeting plan' },
    { step: '04', title: 'Execution', description: 'From preparation to outreach to closing - we support every step' }
  ];

  return (
    <div className="min-h-screen">

      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center justify-center overflow-hidden bg-gradient-to-br from-emerald-600 via-green-600 to-emerald-700">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGRlZnM+PHBhdHRlcm4gaWQ9ImdyaWQiIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSI+PHBhdGggZD0iTSAxMCAwIEwgMCAwIDAgMTAiIGZpbGw9Im5vbmUiIHN0cm9rZT0icmdiYSgyNTUsMjU1LDI1NSwwLjA1KSIgc3Ryb2tlLXdpZHRoPSIxIi8+PC9wYXR0ZXJuPjwvZGVmcz48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSJ1cmwoI2dyaWQpIi8+PC9zdmc+')] opacity-20"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight" data-testid="hero-title">
              Stop Guessing.<br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-200 to-emerald-100">
                Start Raising.
              </span>
            </h1>

            <p className="text-xl md:text-2xl text-green-50 mb-8 leading-relaxed">
              We help founders at every stage—from ideation to pre-Series A—become investor-ready and raise capital through honest guidance and strategic execution.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => navigate('/contact')}
                data-testid="hero-cta-btn"
                className="bg-white text-emerald-700 hover:bg-green-50 text-lg px-8 py-6 shadow-2xl hover:shadow-white/50 transition-all duration-300 hover:scale-105 font-bold"
              >
                Connect With Us
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              <Button
                size="lg"
                variant="outline"
                onClick={() => navigate('/services')}
                data-testid="hero-services-btn"
                className="border-2 border-white text-white hover:bg-white/20 text-lg px-8 py-6 backdrop-blur-sm transition-all duration-300"
              >
                View Our Services
              </Button>
            </div>
          </div>
        </div>

        <div className="absolute top-20 left-10 w-20 h-20 bg-green-300/20 rounded-full blur-xl animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-32 h-32 bg-emerald-300/20 rounded-full blur-xl animate-pulse" style={{ animationDelay: '1s' }}></div>
      </section>

      {/* Enhanced Stats Section */}
      <section className="py-20 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
        <div className="absolute top-0 left-0 w-72 h-72 bg-emerald-100/30 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-amber-100/30 rounded-full blur-3xl"></div>

        <div className="container mx-auto px-4 relative z-10">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-2 bg-gradient-to-r from-emerald-500 to-green-600 text-white text-xs font-bold uppercase tracking-widest rounded-full shadow-lg mb-4">
              Our Track Record
            </span>

            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">
              Numbers That <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-green-600">Speak</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="relative group" data-testid={`stat-${index}`}>
                <div className={`absolute -inset-1 bg-gradient-to-r ${stat.gradient} rounded-3xl blur-xl opacity-30 group-hover:opacity-60 transition-opacity duration-500`}></div>

                <Card className="relative border-2 border-white bg-white/90 backdrop-blur-sm shadow-2xl hover:shadow-3xl transition-all duration-500 hover:-translate-y-2 overflow-hidden">
                  <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${stat.gradient} opacity-10 rounded-full blur-2xl`}></div>

                  <CardContent className="p-8 text-center relative">
                    <div className={`w-16 h-16 mx-auto mb-4 bg-gradient-to-br ${stat.gradient} rounded-2xl flex items-center justify-center shadow-xl ${stat.glow} group-hover:scale-110 group-hover:rotate-6 transition-all duration-300`}>
                      <stat.icon className="h-8 w-8 text-white" strokeWidth={2.5} />
                    </div>

                    <div className={`text-5xl md:text-6xl font-black bg-gradient-to-br ${stat.gradient} bg-clip-text text-transparent mb-2 leading-none`}>
                      <AnimatedCounter end={stat.value} prefix={stat.prefix} suffix={stat.suffix} />
                    </div>

                    <div className="text-gray-700 font-bold text-sm uppercase tracking-wider mt-3">
                      {stat.label}
                    </div>

                    <div className={`mt-4 h-1 w-12 mx-auto bg-gradient-to-r ${stat.gradient} rounded-full`}></div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Expertise Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-widest rounded-full mb-4">
              What We Do
            </span>

            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our <span className="text-emerald-600">Expertise</span>
            </h2>

            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We connect you with the right investors across multiple domains
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {expertise.slice(0, 3).map((item, index) => (
              <Card
                key={index}
                className="border-2 border-gray-100 hover:border-transparent transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 group overflow-hidden cursor-pointer relative"
                data-testid={`expertise-${index}`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${item.bgPattern} opacity-50`}></div>
                <div className={`absolute top-0 right-0 w-40 h-40 bg-gradient-to-br ${item.gradient} opacity-10 rounded-full blur-3xl group-hover:opacity-20 transition-opacity duration-500`}></div>

                <CardContent className="p-8 relative z-10">
                  <div className={`w-20 h-20 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center mb-6 shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                    <item.icon className="h-10 w-10 text-white" strokeWidth={2} />
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>

                  <div className={`mt-6 h-1 w-16 bg-gradient-to-r ${item.gradient} rounded-full group-hover:w-24 transition-all duration-300`}></div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto mt-8">
            {expertise.slice(3).map((item, index) => (
              <Card
                key={index}
                className="border-2 border-gray-100 hover:border-transparent transition-all duration-500 hover:shadow-2xl hover:-translate-y-2 group overflow-hidden cursor-pointer relative"
                data-testid={`expertise-${index + 3}`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${item.bgPattern} opacity-50`}></div>
                <div className={`absolute top-0 right-0 w-40 h-40 bg-gradient-to-br ${item.gradient} opacity-10 rounded-full blur-3xl group-hover:opacity-20 transition-opacity duration-500`}></div>

                <CardContent className="p-8 relative z-10">
                  <div className={`w-20 h-20 bg-gradient-to-br ${item.gradient} rounded-2xl flex items-center justify-center mb-6 shadow-xl group-hover:scale-110 group-hover:rotate-6 transition-all duration-500`}>
                    <item.icon className="h-10 w-10 text-white" strokeWidth={2} />
                  </div>

                  <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>

                  <div className={`mt-6 h-1 w-16 bg-gradient-to-r ${item.gradient} rounded-full group-hover:w-24 transition-all duration-300`}></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-20 bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why <span className="text-emerald-600">FounderScale Capital</span>?
            </h2>

            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              We're not just another fundraising advisor. We're founders helping founders.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {whyChoose.map((item, index) => (
              <Card
                key={index}
                className="border-2 border-gray-100 hover:border-emerald-300 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 group bg-white"
                data-testid={`why-card-${index}`}
              >
                <CardContent className="p-6">
                  <div className="bg-gradient-to-br from-emerald-500 to-green-600 w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300 shadow-lg">
                    <item.icon className="h-7 w-7 text-white" />
                  </div>

                  <h3 className="text-lg font-bold text-gray-900 mb-3">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed text-sm">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Fundraising Support for Founders */}
      <section className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">

            <div className="text-center mb-12">
              <span className="inline-block px-4 py-2 bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-widest rounded-full mb-4">
                Fundraising Support
              </span>

              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                Helping Founders Become
                <span className="text-emerald-600"> Investor-Ready</span>
              </h2>
            </div>

            <div className="space-y-8 text-gray-600 text-lg leading-relaxed">
              <p>
                FounderScale Capital works with founders who are preparing to raise capital
                and need a structured approach to becoming investor-ready. From ideation to
                pre-Series A, our work is focused on helping founders understand what they
                need to communicate to investors and how to approach the fundraising process
                with greater clarity.
              </p>

              <p>
                Fundraising is more than creating a pitch deck or sending messages to
                investors. Founders need a clear fundraising strategy, relevant investor
                targeting, strong documentation, financial projections, and a consistent
                approach to investor communication. FounderScale Capital brings these
                elements together as part of an end-to-end fundraising support process.
              </p>

              <p>
                Our services include fundraising assistance, investor discovery, investor
                outreach, pitch deck preparation, financial modeling, and data room
                preparation. We also help founders develop an investor targeting strategy
                based on their business, stage, fundraising requirements, and the type of
                capital they are seeking.
              </p>

              <p>
                FounderScale Capital works across sectors including Fintech, Healthcare,
                EdTech, SaaS, E-commerce, Blockchain, AI/ML, CleanTech, BioTech, and
                Enterprise Software. Each engagement begins with understanding the business,
                its current stage, traction, objectives, and fundraising requirements rather
                than applying a standard process to every company.
              </p>

              <p>
                Our approach starts with a discovery conversation followed by an honest
                assessment. If there is a fit, we develop a fundraising strategy and investor
                targeting plan before moving into execution. This can include preparing
                fundraising materials, identifying relevant investors, supporting outreach,
                and helping founders navigate the process as conversations progress.
              </p>

              <p>
                We also work with investors and investment vehicles through deal origination,
                startup scouting, startup matchmaking, fundraising support, and strategic
                acquisition opportunities. This allows FounderScale Capital to work across
                both sides of the startup and investment ecosystem.
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">

              <Card className="border-2 border-emerald-100 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Investor Readiness
                  </h3>

                  <p className="text-gray-600 leading-relaxed">
                    Build the materials, financial information, and fundraising strategy
                    needed to communicate your opportunity clearly.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-emerald-100 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Investor Targeting
                  </h3>

                  <p className="text-gray-600 leading-relaxed">
                    Identify relevant investors based on your business, stage, sector,
                    and fundraising requirements.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2 border-emerald-100 bg-white">
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    Fundraising Execution
                  </h3>

                  <p className="text-gray-600 leading-relaxed">
                    Support the fundraising journey from preparation and outreach through
                    ongoing investor conversations.
                  </p>
                </CardContent>
              </Card>

            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">

          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Industries We Serve
            </h2>

            <p className="text-xl text-gray-600">
              Supporting founders across diverse sectors
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-4 max-w-5xl mx-auto">
            {industries.map((industry, index) => (
              <div
                key={index}
                className="px-6 py-3 bg-white border-2 border-emerald-200 rounded-full text-emerald-700 font-semibold hover:bg-gradient-to-r hover:from-emerald-500 hover:to-green-600 hover:text-white hover:border-transparent transition-all duration-300 cursor-pointer hover:scale-105 shadow-sm hover:shadow-md"
              >
                {industry}
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* Process */}
      <section className="py-20 bg-gradient-to-br from-slate-50 to-white">
        <div className="container mx-auto px-4">

          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              How We Work
            </h2>

            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              A transparent, founder-first approach to fundraising
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {process.map((item, index) => (
              <div key={index} className="relative">

                <div className="text-center group">
                  <div className="relative inline-block mb-6">
                    <div className="text-6xl font-bold text-emerald-100 group-hover:text-emerald-200 transition-colors duration-300">
                      {item.step}
                    </div>
                  </div>

                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {item.title}
                  </h3>

                  <p className="text-gray-600">
                    {item.description}
                  </p>
                </div>

                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-12 -right-4 w-8 h-0.5 bg-gradient-to-r from-emerald-300 to-transparent"></div>
                )}

              </div>
            ))}
          </div>

        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">

          <div className="text-center mb-16">
            <span className="inline-block px-4 py-2 bg-emerald-100 text-emerald-700 text-xs font-bold uppercase tracking-widest rounded-full mb-4">
              Frequently Asked Questions
            </span>

            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Questions Founders <span className="text-emerald-600">Ask Us</span>
            </h2>

            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Clear answers about fundraising support, investor access, and how FounderScale Capital works with founders.
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">

            <details className="group border-2 border-gray-100 rounded-2xl bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
              <summary className="flex items-center justify-between cursor-pointer list-none p-6 text-lg font-bold text-gray-900">
                What does FounderScale Capital do?
                <span className="text-emerald-600 text-2xl group-open:rotate-45 transition-transform duration-300">
                  +
                </span>
              </summary>

              <div className="px-6 pb-6 text-gray-600 leading-relaxed">
                FounderScale Capital helps founders become investor-ready and raise capital through
                fundraising strategy, investor discovery, investor outreach, documentation, and
                end-to-end fundraising support. The firm works with founders from ideation through
                pre-Series A.
              </div>
            </details>

            <details className="group border-2 border-gray-100 rounded-2xl bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
              <summary className="flex items-center justify-between cursor-pointer list-none p-6 text-lg font-bold text-gray-900">
                What stages of startups does FounderScale Capital work with?
                <span className="text-emerald-600 text-2xl group-open:rotate-45 transition-transform duration-300">
                  +
                </span>
              </summary>

              <div className="px-6 pb-6 text-gray-600 leading-relaxed">
                FounderScale Capital supports founders across different stages, from ideation and
                early-stage businesses through growth-stage and pre-Series A fundraising. The
                approach is tailored to the company's stage, business, traction, and fundraising
                requirements.
              </div>
            </details>

            <details className="group border-2 border-gray-100 rounded-2xl bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
              <summary className="flex items-center justify-between cursor-pointer list-none p-6 text-lg font-bold text-gray-900">
                What fundraising services does FounderScale Capital provide?
                <span className="text-emerald-600 text-2xl group-open:rotate-45 transition-transform duration-300">
                  +
                </span>
              </summary>

              <div className="px-6 pb-6 text-gray-600 leading-relaxed">
                Services include fundraising assistance, investor discovery, investor outreach,
                pitch deck preparation, financial modeling, and data room preparation. FounderScale
                Capital also provides strategic support throughout the fundraising process.
              </div>
            </details>

            <details className="group border-2 border-gray-100 rounded-2xl bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
              <summary className="flex items-center justify-between cursor-pointer list-none p-6 text-lg font-bold text-gray-900">
                How does the fundraising process work?
                <span className="text-emerald-600 text-2xl group-open:rotate-45 transition-transform duration-300">
                  +
                </span>
              </summary>

              <div className="px-6 pb-6 text-gray-600 leading-relaxed">
                The process starts with a discovery call to understand the business, traction, and
                fundraising goals. This is followed by an honest assessment, strategy design and
                investor targeting, and then execution covering preparation, outreach, and support
                through the fundraising process.
              </div>
            </details>

            <details className="group border-2 border-gray-100 rounded-2xl bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
              <summary className="flex items-center justify-between cursor-pointer list-none p-6 text-lg font-bold text-gray-900">
                Does FounderScale Capital work with investors as well as startups?
                <span className="text-emerald-600 text-2xl group-open:rotate-45 transition-transform duration-300">
                  +
                </span>
              </summary>

              <div className="px-6 pb-6 text-gray-600 leading-relaxed">
                Yes. FounderScale Capital also works on the investor side through deal origination,
                startup scouting, startup matchmaking, fundraising support for investment vehicles,
                and strategic acquisition opportunities.
              </div>
            </details>

            <details className="group border-2 border-gray-100 rounded-2xl bg-white shadow-sm hover:shadow-md transition-shadow duration-300">
              <summary className="flex items-center justify-between cursor-pointer list-none p-6 text-lg font-bold text-gray-900">
                What makes FounderScale Capital's approach different?
                <span className="text-emerald-600 text-2xl group-open:rotate-45 transition-transform duration-300">
                  +
                </span>
              </summary>

              <div className="px-6 pb-6 text-gray-600 leading-relaxed">
                FounderScale Capital takes a founder-first approach focused on understanding the
                business before taking on an engagement. The company emphasizes honest assessment,
                private one-to-one communication, iterative document support, investor network
                development, and end-to-end partnership.
              </div>
            </details>

          </div>
        </div>
      </section>

      {/* Founders */}
      <section className="py-20 bg-gradient-to-br from-emerald-700 via-green-700 to-emerald-800">
        <div className="container mx-auto px-4">

          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Meet Our Founders
            </h2>

            <p className="text-xl text-green-100 max-w-2xl mx-auto">
              Experienced operators who've been in your shoes
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto">

            <Card className="bg-white/10 backdrop-blur-lg border-green-300/20 hover:bg-white/20 transition-all duration-300 hover:-translate-y-2 shadow-2xl overflow-hidden">
              <div className="h-80 overflow-hidden bg-gradient-to-br from-emerald-600 to-green-700">
                <img
                  src="https://customer-assets.emergentagent.com/job_investment-platform-19/artifacts/7o5ljlv3_WhatsApp%20Image%202026-05-25%20at%206.49.02%20PM.jpeg"
                  alt="Arshdeep Singh"
                  className="w-full h-full object-cover"
                />
              </div>

              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold text-white mb-2">
                  Arshdeep Singh
                </h3>

                <p className="text-green-200 font-semibold mb-4">
                  CEO & Co-Founder
                </p>

                <p className="text-green-50 leading-relaxed">
                  Passionate about helping founders navigate the complex world of fundraising with clarity and confidence.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white/10 backdrop-blur-lg border-green-300/20 hover:bg-white/20 transition-all duration-300 hover:-translate-y-2 shadow-2xl overflow-hidden">
              <div className="h-80 overflow-hidden bg-gradient-to-br from-emerald-600 to-green-700">
                <img
                  src="https://customer-assets.emergentagent.com/job_investment-platform-19/artifacts/shggoqn5_WhatsApp%20Image%202026-05-25%20at%206.49.03%20PM.jpeg"
                  alt="Mohd Sajid"
                  className="w-full h-full object-cover"
                />
              </div>

              <CardContent className="p-8 text-center">
                <h3 className="text-2xl font-bold text-white mb-2">
                  Mohd Sajid
                </h3>

                <p className="text-green-200 font-semibold mb-4">
                  COO & Co-Founder
                </p>

                <p className="text-green-50 leading-relaxed">
                  Expert in building scalable fundraising processes and connecting founders with the right investors.
                </p>
              </CardContent>
            </Card>

          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-emerald-600 to-green-600">
        <div className="container mx-auto px-4 text-center">

          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Become Fundable?
          </h2>

          <p className="text-xl text-green-50 mb-8 max-w-2xl mx-auto">
            Let's have an honest conversation about your fundraising journey. No pressure, just real talk.
          </p>

          <Button
            size="lg"
            onClick={() => navigate('/contact')}
            data-testid="bottom-cta-btn"
            className="bg-white text-emerald-700 hover:bg-green-50 text-lg px-12 py-6 shadow-2xl hover:shadow-white/50 transition-all duration-300 hover:scale-105 font-bold"
          >
            Connect With Us
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>

        </div>
      </section>

    </div>
  );
};

export default Home;
